import { useMemo } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
// Imported from its own path, not the barrel: specs mock `@shared/utils/eventInfo` wholesale to
// avoid loading its provider graph, and a barrel import would come back undefined under that mock.
import { isEventReadPending } from '@shared/utils/eventInfo/isEventReadPending';
import { EventType } from '@graphql/generated-base';
import { useHoneymoonSearchUrl } from '@shared/hooks/useHoneymoonSearchUrl/useHoneymoonSearchUrl';
import { useCashFundAccess } from '@shared/hooks/useCashFundAccess/useCashFundAccess';
import { getHoneymoonPromoNumbers } from '@shared/hooks/useTravelHoneymoonConfig/getHoneymoonPromoNumbers';
import { useFundBalanceDisplay } from '@shared/utils/cashFund/useFundBalanceDisplay';
import { useFundCouponSavings } from '@shared/utils/cashFund/useFundCouponSavings';
import type { HoneymoonOffersPromoResult } from './useHoneymoonOffersPromo.types';
import type { HoneymoonUtmOverrides } from '@shared/hooks/useHoneymoonSearchUrl/useHoneymoonSearchUrl.types';

type Args = Readonly<{
  eventHandle: string;
  /** Page-specific UTM so each entry point (Cash Received widget, Cash Funds dashboard) is tracked separately. */
  utmOverrides?: HoneymoonUtmOverrides;
}>;

// Gates the honeymoon cash-fund promo slider and builds the "Unlock Honeymoon Offers" URL.
//
// Order mirrors the guest-side useCashFundHotelSearch: role, then the flag, then the balance.
// Eligibility before the experiment is wedding + couple — both decided without the balance query,
// so the Amplitude exposure cohort is the people the promo is actually for. A registry editor or
// staff member on someone else's event is not part of that cohort and must not be bucketed.
//
// The balance is deliberately read after the flag, and it is the travel fund rather than the Stripe
// Connect payout balance: Connect money can be withdrawn to a bank but cannot pay for a stay, so
// gating on it promised offers the checkout would then refuse.
export const useHoneymoonOffersPromo = ({ eventHandle, utmOverrides }: Args): HoneymoonOffersPromoResult => {
  const eventInfoResult = useEventInfo();
  const { eventInfo } = eventInfoResult;
  // Ahead of every gate below, all of which key on the event: `isWedding` reads false before the
  // provider populates, which committed both promo surfaces to hidden for a frame.
  const isEventPending = isEventReadPending(eventInfoResult);
  const eventId = eventInfo?.eventId;
  const isWedding = eventInfo?.eventType === EventType.wedding;

  // Both partners qualify; staff on someone else's event come back as on-behalf support. Passing no
  // eventId on a non-wedding skips the role query outright, so a baby registry pays nothing for it.
  const { isCouple, isSuperAdminOnBehalf, isLoading: isRoleLoading } = useCashFundAccess({ eventId: isWedding ? eventId : undefined });

  // Skipped for anyone but the couple, so the exposure fires only for the intended cohort.
  const [isTreatment, isFlagLoading] = useIsFeatureEnabled('hotelsSearchCashFundExperiment', { skip: !isWedding || !isCouple });

  // Support never enters the experiment — no flag read, so no exposure to skew the cohort — but it
  // still gets the surface, matching useCashFundHotelSearch on the destination route.
  const isCashFundSurfaceEnabled = isWedding && (isTreatment || isSuperAdminOnBehalf);
  const {
    unlockOffersUrl,
    buildUnlockOffersUrl,
    defaultTrip,
    isUrlReady,
    hasStartingLocation,
    isFundCurrencySupported,
    bestCashFundCoupons,
    fundBalanceMinorUnits,
    fundCurrency,
    honeymoonConfig,
  } = useHoneymoonSearchUrl({
    eventHandle,
    eventId,
    isCashFundSurfaceEnabled,
    utmOverrides,
  });

  // Memoised because it feeds useHoneymoonPromoTelemetry's useMemo dependency.
  const fundBalance = useMemo(() => ({ valueInMinorUnits: fundBalanceMinorUnits, currencyCode: fundCurrency }), [fundBalanceMinorUnits, fundCurrency]);
  const fundBalanceDisplay = useFundBalanceDisplay(fundBalance);

  // The currency must exist AND be one the hotel search prices in. Without it the search URL drops
  // its `currency` param; outside the travel allowlist the param is dropped on arrival instead, and
  // either way the destination's gate refuses the fund. See `isFundCurrencySupported`.
  const hasUsableFund = fundBalanceMinorUnits > 0 && !!fundCurrency && isFundCurrencySupported;

  // Derived from the config the URL hook already read: a second flag read fires a second Amplitude
  // exposure for the same view, and the exposure queue does not dedupe.
  const { maxSavingsDisplay, promoSliderPercentOff } = getHoneymoonPromoNumbers(honeymoonConfig);

  // The couple's real code: its rate, its cap, its currency. `percentOff` comes back undefined once
  // the cap bites, which is what tells a surface to stop quoting a rate.
  const {
    display: fundCouponSavingsDisplay,
    percentOff: fundCouponPercentOff,
    code: fundCouponCode,
  } = useFundCouponSavings(fundBalance, bestCashFundCoupons, honeymoonConfig.minCouponSavingToShowMajorUnits);

  // The three facts a surface needs before it can quote an offer and send the couple somewhere to
  // pick a trip: a discount figure, a balance to name, and an event to build the link from.
  const canOpenTripPicker = !!promoSliderPercentOff && !!fundBalanceDisplay && !!eventId;

  return {
    // With no configured destination the couple must pick one, so there has to be somewhere that
    // asks — otherwise the CTA would push a placeless link and land them on wedding-venue hotels.
    isEnabled: isCashFundSurfaceEnabled && isUrlReady && hasUsableFund && (hasStartingLocation || canOpenTripPicker),
    unlockOffersUrl,
    buildUnlockOffersUrl,
    defaultTrip,
    fundBalance,
    fundBalanceDisplay,
    hasStartingLocation,
    maxSavingsDisplay,
    promoSliderPercentOff,
    fundCouponSavingsDisplay,
    fundCouponPercentOff,
    fundCouponCode,
    showDialogMinFundBalanceMajorUnits: honeymoonConfig.showDialogMinFundBalanceMajorUnits,
    // Only the reads that gate the decision, in the order they gate it: a settled non-wedding is an
    // answer, not a wait. Mirrors useCashFundHotelSearch's own isLoading.
    isLoading: isEventPending || (isWedding && (isRoleLoading || (isCouple && isFlagLoading) || (isCashFundSurfaceEnabled && !isUrlReady))),
  };
};
